/**
 * EmbeddedReportCheckout v1.0 (2026-10-23, cc_tasks row 978, T972)
 *
 * Pays for ONE Full Report on one company inside the dialog that shows it, the same way the public Snapshot drawer
 * does (public/order-report.js v3.7, T962): run-service report_order "create" with embedded: true answers a Stripe
 * embedded checkout (ui_mode embedded, redirect_on_completion never) and the form mounts here. No plan change, no
 * credits involved. If the form cannot load, the hosted payment page opens in a NEW tab (report_order "resume" with
 * fallback: true expires the embedded session first, so only one can be paid) and this page never moves.
 * When the payment completes, the order page (/report-order/<id>) is offered in a new tab: it confirms the payment
 * and shows the report as it gets ready. The webhook, not this component, decides that the order is paid.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL } from "@/lib/supabaseKeys";
import { acquisition } from "@/lib/acquisition";

// The publishable key of the account that holds the Full Report price: public by design (it only names the account
// to Stripe.js; every amount, line and session is made on the server). Same value as public/order-report.js STRIPE_PK.
const STRIPE_PK = "pk_live_51T1QRQGtJvMqFsfxDoBZ3EIK3AhfJzpYkpKaiqBmLczYghsMVi0m763XSsOyQzz3cI26GaW34AOOUfVDG7UouZfq00JDxY3tKX";
const STRIPE_JS = "https://js.stripe.com/v3/";

type Stripeish = { initEmbeddedCheckout: (o: { fetchClientSecret: () => Promise<string>; onComplete?: () => void }) => Promise<{ mount: (el: HTMLElement) => void; destroy: () => void }> };

function loadStripe(): Promise<Stripeish | null> {
  const w = window as unknown as { Stripe?: (pk: string) => Stripeish };
  if (w.Stripe) return Promise.resolve(w.Stripe(STRIPE_PK));
  return new Promise((resolve) => {
    let s = document.querySelector<HTMLScriptElement>(`script[src="${STRIPE_JS}"]`);
    if (!s) { s = document.createElement("script"); s.src = STRIPE_JS; s.async = true; document.head.appendChild(s); }
    const done = () => resolve(w.Stripe ? w.Stripe(STRIPE_PK) : null);
    s.addEventListener("load", done, { once: true });
    s.addEventListener("error", () => resolve(null), { once: true });
    setTimeout(done, 10000);
  });
}

async function orderCall(body: Record<string, unknown>): Promise<any> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const { data } = await supabase.auth.getSession();
  if (data?.session?.access_token) headers.Authorization = `Bearer ${data.session.access_token}`;
  const res = await fetch(`${SUPABASE_URL}/functions/v1/run-service/report_order`, { method: "POST", headers, body: JSON.stringify(body) });
  return res.json().catch(() => ({}));
}

type Phase = "starting" | "form" | "fallback" | "paid" | "error";

export function EmbeddedReportCheckout({ entityName, entityDomain, pagePath, onBack }: {
  entityName: string;
  entityDomain?: string | null;
  pagePath?: string;
  onBack: () => void;
}) {
  const [phase, setPhase] = useState<Phase>("starting");
  const [orderId, setOrderId] = useState<string | null>(null);
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const checkoutRef = useRef<{ destroy: () => void } | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const j = await orderCall({
        action: "create", embedded: true, entity_name: entityName, entity_domain: entityDomain || undefined,
        page_family: "company_vetter", page_path: pagePath || "/company-vetter", acq: acquisition(),
      }).catch(() => ({}));
      if (!live) return;
      if (!j?.order_id) { setPhase("error"); return; }
      setOrderId(j.order_id);
      // Paid with credits the account already has: nothing to pay here.
      if (j.paid_with) { setPhase("paid"); return; }
      const fallback = async (reason: string) => {
        const r = await orderCall({ action: "resume", order_id: j.order_id, page_path: pagePath || "/company-vetter", fallback: true, reason }).catch(() => ({}));
        if (!live) return;
        if (r?.url) {
          setFallbackUrl(r.url);
          try { const t = window.open(r.url, "_blank"); if (t) t.opener = null; } catch { /* the link below still works */ }
          setPhase("fallback");
        } else setPhase("error");
      };
      if (!j.client_secret) { void fallback("no_client_secret"); return; }
      const stripe = await loadStripe();
      if (!live) return;
      if (!stripe) { void fallback("stripe_js_unavailable"); return; }
      try {
        setPhase("form");
        const co = await stripe.initEmbeddedCheckout({
          fetchClientSecret: async () => j.client_secret as string,
          onComplete: () => { if (live) setPhase("paid"); },
        });
        if (!live) { co.destroy(); return; }
        checkoutRef.current = co;
        if (mountRef.current) co.mount(mountRef.current);
      } catch { void fallback("embedded_init_failed"); }
    })();
    return () => { live = false; try { checkoutRef.current?.destroy(); } catch { /* already gone */ } };
  }, [entityName, entityDomain, pagePath]);

  const orderHref = orderId ? `/report-order/${orderId}` : null;
  return (
    <div className="space-y-3" title={`Pay once for the Full Report on ${entityName}`}>
      <button type="button" onClick={onBack} className="text-sm font-medium text-primary underline-offset-4 hover:underline" title="Back to the options. Nothing is charged unless you pay.">
        Back to the options
      </button>
      {phase === "starting" && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status" title="Preparing the secure payment form">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading the secure payment form…
        </p>
      )}
      <div ref={mountRef} className={phase === "form" ? "rounded-md bg-white overflow-hidden min-h-[120px]" : "hidden"} />
      {phase === "fallback" && (
        <p className="text-sm text-muted-foreground" role="status" title="The payment page opened in another tab">
          The secure payment page is open in a new tab. This page stays here.{" "}
          {fallbackUrl && <a href={fallbackUrl} target="_blank" rel="noopener" className="text-primary underline-offset-4 hover:underline" title="Open the secure payment page again, in a new tab">Open it again</a>}
        </p>
      )}
      {phase === "paid" && (
        <div className="rounded-md border p-3 text-sm" role="status" title="Your order is in">
          <p className="flex items-center gap-2 font-medium"><CheckCircle2 className="h-4 w-4 text-green-600" /> {`Ordered. The Full Report on ${entityName} is being written.`}</p>
          {orderHref && (
            <a href={orderHref} target="_blank" rel="noopener" className="mt-1 inline-block text-primary underline-offset-4 hover:underline" title="Your order page: it confirms the payment and shows the report as soon as it is ready. Opens in a new tab.">
              Open your order page
            </a>
          )}
        </div>
      )}
      {phase === "error" && (
        <p className="text-sm text-destructive" role="status" title="The payment form could not be opened">
          The payment form could not load just now. Please try again in a minute.
        </p>
      )}
    </div>
  );
}
